import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

// Chronos Orange preset — base Aura com cor primária Deep Orange (#ff5722)
// e overrides de componentes para manter coerência visual com o look v17.
//
// Abordagem: em vez de sobrescrever via CSS `!important`, este preset usa
// o sistema de design tokens de PrimeNG v20. Cada componente expõe tokens
// nomeados (organizados em seções `root`, `option`, `header`, etc.) que
// o engine do preset injeta como variáveis CSS no runtime. É o caminho
// idiomático e não conflita com upgrades de versão do PrimeNG.

const primaryColor = '#ff5722';
const primaryDark = '#e64a19';

// Light mode neutrals
const textNormal = '#334155'; // slate-700
const textOnPrimary = '#ffffff';
const borderSubtle = '#d7d8d9';
const surfaceSubtle = '#fdfeff';

// Dark mode neutrals — paleta ZINC alinhada com Ultima PrimeNG
// (https://ultima.primeng.org). Zinc é uma escala neutra (vs slate que
// tem tons azulados), dando um dark mais "preto natural" que dito
// aconchegante nos dashboards do Ultima.
//
// Hierarchy:
//   body              → zinc-950 (#09090b) — page background
//   card / form-field → zinc-900 (#18181b) — elevated surface
//   border            → zinc-700 (#3f3f46)
//   hover/option      → zinc-800 (#27272a)
//   text              → white
//   text muted        → zinc-400 (#a1a1aa)
const darkSurface = '#18181b'; // zinc-900 — card/form field background
const darkSurfaceHover = '#27272a'; // zinc-800 — hover/option highlight
const darkBorder = '#3f3f46'; // zinc-700
const darkBorderHover = '#52525b'; // zinc-600
const darkText = '#ffffff'; // white
const darkTextMuted = '#a1a1aa'; // zinc-400

export const OrangePreset = definePreset(Aura, {
  semantic: {
    // Paleta Material Deep Orange completa com gradiente real.
    // Setar primary.50-400 para valores mais claros evita que elementos
    // "sutis" do chrome do datepicker (dias do mês anterior/próximo,
    // bordas, hover leve) fiquem banhados em laranja saturado. Só
    // primary.500 é o #ff5722 puro; 600+ é o hover/active darker.
    primary: {
      50: '#fbe9e7',
      100: '#ffccbc',
      200: '#ffab91',
      300: '#ff8a65',
      400: '#ff7043',
      500: '#ff5722', // brand
      600: '#f4511e',
      700: '#e64a19', // hover/active
      800: '#d84315',
      900: '#bf360c',
      950: '#900000',
    },
    // formField — não precisa override de paddingX/Y porque o html
    // font-size está em 14px (configurado em styles.scss @layer reset),
    // então o default Aura paddingX 0.75rem = 10.5px e paddingY 0.5rem
    // = 7px já dão a altura compacta esperada. Mesma estratégia do
    // template oficial Apollo do PrimeNG.
    //
    // Surface palette — ZINC em ambos light e dark (em vez do slate
    // default do Aura). Zinc é uma escala neutra sem tons azulados,
    // batendo com o template Ultima PrimeNG.
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#fafafa',
          100: '#f4f4f5',
          200: '#e4e4e7',
          300: '#d4d4d8',
          400: '#a1a1aa',
          500: '#71717a',
          600: '#52525b',
          700: '#3f3f46',
          800: '#27272a',
          900: '#18181b',
          950: '#09090b',
        },
      },
      dark: {
        surface: {
          0: '#ffffff',
          50: '#fafafa',
          100: '#f4f4f5',
          200: '#e4e4e7',
          300: '#d4d4d8',
          400: '#a1a1aa',
          500: '#71717a',
          600: '#52525b',
          700: '#3f3f46',
          800: '#27272a',
          900: '#18181b',
          950: '#09090b',
        },
      },
    },
  },
  components: {
    inputtext: {
      colorScheme: {
        light: {
          root: {
            color: textNormal,
            background: surfaceSubtle,
            borderColor: borderSubtle,
            hoverBorderColor: primaryColor,
            focusBorderColor: primaryColor,
          },
        },
        dark: {
          root: {
            color: darkText,
            background: darkSurface,
            borderColor: darkBorder,
            hoverBorderColor: primaryColor,
            focusBorderColor: primaryColor,
          },
        },
      },
    },
    // datepicker.root só tem transitionDuration; cores/bordas vêm de inputtext.
    // Customizar apenas o painel popup aqui se necessário no futuro.
    select: {
      colorScheme: {
        light: {
          root: {
            color: textNormal,
            background: surfaceSubtle,
            borderColor: borderSubtle,
            hoverBorderColor: primaryColor,
            focusBorderColor: primaryColor,
          },
          // Chevron icon (p-select-dropdown): Aura default é slate-400
          // que fica quase invisível. Escurecer pra slate-600/textNormal
          // pra dar contraste legível.
          dropdown: {
            color: textNormal,
          },
          option: {
            color: textNormal,
            selectedColor: textOnPrimary,
            selectedFocusColor: textOnPrimary,
            selectedBackground: primaryColor,
            selectedFocusBackground: primaryDark,
          },
        },
        dark: {
          root: {
            color: darkText,
            background: darkSurface,
            borderColor: darkBorder,
            hoverBorderColor: primaryColor,
            focusBorderColor: primaryColor,
          },
          dropdown: {
            color: darkTextMuted,
          },
          option: {
            color: darkText,
            selectedColor: textOnPrimary,
            selectedFocusColor: textOnPrimary,
            selectedBackground: primaryColor,
            selectedFocusBackground: primaryDark,
          },
        },
      },
    },
    multiselect: {
      colorScheme: {
        light: {
          root: {
            color: textNormal,
            background: surfaceSubtle,
            borderColor: borderSubtle,
            hoverBorderColor: primaryColor,
            focusBorderColor: primaryColor,
          },
          option: {
            color: textNormal,
            selectedColor: textOnPrimary,
            selectedFocusColor: textOnPrimary,
          },
        },
        dark: {
          root: {
            color: darkText,
            background: darkSurface,
            borderColor: darkBorder,
            hoverBorderColor: primaryColor,
            focusBorderColor: primaryColor,
          },
          option: {
            color: darkText,
            selectedColor: textOnPrimary,
            selectedFocusColor: textOnPrimary,
          },
        },
      },
    },
    autocomplete: {
      colorScheme: {
        light: {
          root: {
            color: textNormal,
            background: surfaceSubtle,
            borderColor: borderSubtle,
            hoverBorderColor: primaryColor,
            focusBorderColor: primaryColor,
          },
          option: {
            color: textNormal,
            selectedColor: textOnPrimary,
            selectedFocusColor: textOnPrimary,
            selectedBackground: primaryColor,
            selectedFocusBackground: primaryDark,
          },
          // v17 pintava o botão dropdown do autocomplete em laranja CTA,
          // diferente do select que usa chevron neutro. Tokens específicos
          // do dropdown deixam isso explícito no preset. IMPORTANTE: precisa
          // setar hoverColor/activeColor explicitamente senão o Aura default
          // (slate-700) vira cinza no chevron branco quando faz hover.
          dropdown: {
            background: primaryColor,
            hoverBackground: primaryDark,
            activeBackground: primaryDark,
            color: textOnPrimary,
            hoverColor: textOnPrimary,
            activeColor: textOnPrimary,
            borderColor: primaryColor,
            hoverBorderColor: primaryDark,
          },
        },
        dark: {
          root: {
            color: darkText,
            background: darkSurface,
            borderColor: darkBorder,
            hoverBorderColor: primaryColor,
            focusBorderColor: primaryColor,
          },
          option: {
            color: darkText,
            selectedColor: textOnPrimary,
            selectedFocusColor: textOnPrimary,
            selectedBackground: primaryColor,
            selectedFocusBackground: primaryDark,
          },
          dropdown: {
            background: primaryColor,
            hoverBackground: primaryDark,
            activeBackground: primaryDark,
            color: textOnPrimary,
            hoverColor: textOnPrimary,
            activeColor: textOnPrimary,
            borderColor: primaryColor,
            hoverBorderColor: primaryDark,
          },
        },
      },
    },
    datepicker: {
      // Tamanho intermediário entre o default Aura (muito espaçoso) e o
      // compacto agressivo. Feedback iterativo: "entre esse e como tava
      // antes nem tao pequeno nem tao grande".
      date: {
        width: '2rem',
        height: '2rem',
        padding: '0.25rem',
      },
      weekDay: {
        padding: '0.375rem',
      },
      dayView: {
        margin: '0.375rem 0 0 0',
      },
      colorScheme: {
        light: {
          panel: {
            background: '#ffffff',
            borderColor: '#e2e8f0',
            color: textNormal,
            padding: '0.625rem',
            // shadow deixado pro Aura default (overlay.popover.shadow) —
            // popup ganha elevação, inline é zerado automaticamente pela
            // regra built-in `.p-datepicker-panel-inline { box-shadow: none }`
            // do v20 (cascata: selector mais específico depois no CSS vence).
            borderRadius: '6px',
          },
          header: {
            background: '#ffffff',
            borderColor: '#e2e8f0',
            color: textNormal,
            padding: '0 0 0.4375rem 0',
          },
        },
        dark: {
          panel: {
            background: darkSurface,
            borderColor: darkBorder,
            color: darkText,
            padding: '0.625rem',
            borderRadius: '6px',
          },
          header: {
            background: darkSurface,
            borderColor: darkBorder,
            color: darkText,
            padding: '0 0 0.4375rem 0',
          },
        },
      },
    },
    // inputnumber.root só tem transitionDuration; cores/bordas vêm de inputtext.
    // textarea: conflito de tipos no TS strict; cores/bordas vêm de inputtext
    // se a Textarea for usada via pInputText/textarea, ou pode ser customizada
    // em etapa futura via refinamento de tipos do preset.
    button: {
      // Padrão Apollo: border-radius compatível com form field (6px) e
      // paddings herdados de form.field.padding.{x,y}. Tokens root só
      // ajustam o que difere do Aura default.
      root: {
        borderRadius: '{form.field.border.radius}',
      },
      colorScheme: {
        light: {
          root: {
            // Apollo secondary = cinza slate médio-escuro com texto branco.
            // Aura default usa surface.100 (quase branco) que fica invisível
            // em cima do fundo branco do form. Slate-500/600 dá contraste
            // consistente com o look Apollo uikit/button.
            secondary: {
              background: '#64748b', // slate-500
              hoverBackground: '#475569', // slate-600
              activeBackground: '#334155', // slate-700
              borderColor: '#64748b',
              hoverBorderColor: '#475569',
              activeBorderColor: '#334155',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: '#64748b',
                shadow: 'none',
              },
            },
          },
          // Variant outlined.secondary — borda slate com texto slate,
          // fundo transparente. Usado em botões menos enfáticos que o
          // filled mas mais presentes que o text-only.
          outlined: {
            secondary: {
              borderColor: '#cbd5e1', // slate-300
              color: '#475569', // slate-600
              hoverBackground: '#f1f5f9', // slate-100
              activeBackground: '#e2e8f0', // slate-200
            },
          },
          // Variant text.secondary — só label, sem fundo/borda. Usado
          // pra ações terciárias (ex: "Voltar" em wizards, links).
          text: {
            secondary: {
              color: '#475569', // slate-600
              hoverBackground: '#f1f5f9', // slate-100
              activeBackground: '#e2e8f0', // slate-200
            },
          },
        },
        dark: {
          root: {
            secondary: {
              background: darkSurfaceHover, // zinc-800
              hoverBackground: darkBorderHover, // zinc-600
              activeBackground: darkBorder, // zinc-700
              borderColor: darkBorderHover, // zinc-600 — distinto do bg (zinc-800)
              hoverBorderColor: darkText,
              activeBorderColor: darkBorder,
              color: darkText,
              hoverColor: darkText,
              activeColor: darkText,
              focusRing: {
                color: darkBorderHover,
                shadow: 'none',
              },
            },
          },
          outlined: {
            secondary: {
              borderColor: darkBorderHover,
              color: darkTextMuted,
              hoverBackground: darkSurfaceHover,
              activeBackground: darkBorder,
            },
          },
          text: {
            secondary: {
              color: darkTextMuted,
              hoverBackground: darkSurfaceHover,
              activeBackground: darkBorder,
            },
          },
        },
      },
    },
    togglebutton: {
      // Aura default usa pill branca interna quando checked (content.
      // checkedBackground: surface.0). Pra match o look v17 (filled
      // orange sólido quando selecionado), o checked state inteiro usa
      // primary color.
      colorScheme: {
        light: {
          root: {
            checkedBackground: primaryColor,
            checkedBorderColor: primaryColor,
            checkedColor: textOnPrimary,
          },
          content: {
            checkedBackground: primaryColor,
          },
        },
        dark: {
          root: {
            checkedBackground: primaryColor,
            checkedBorderColor: primaryColor,
            checkedColor: textOnPrimary,
          },
          content: {
            checkedBackground: primaryColor,
          },
        },
      },
    },
    dialog: {
      // Aura default content.padding é "0 p p p" (top = 0) — parte da
      // premissa de header/content sem background diferenciado. No
      // Chronos o header tem fundo slate escuro (shim em styles.scss),
      // então o content precisa de padding-top real pra não grudar.
      // Substitui pra padding em todos os 4 lados.
      content: {
        padding: '{overlay.modal.padding}',
      },
    },
    datatable: {
      // Tabela mais compacta — feedback "não fica assim" mostrando reference
      // PrimeNG com rows menores. Reduz padding de header e body cells para
      // diminuir altura visual das linhas.
      headerCell: {
        padding: '0.5rem 0.75rem',
      },
      bodyCell: {
        padding: '0.5rem 0.75rem',
      },
      colorScheme: {
        light: {
          headerCell: {
            color: textNormal,
            selectedBackground: primaryColor,
            selectedColor: textOnPrimary,
          },
        },
        dark: {
          headerCell: {
            color: darkText,
            selectedBackground: primaryColor,
            selectedColor: textOnPrimary,
          },
        },
      },
    },
  },
});
